import { ZoneType } from "@fairfleet/geotab";
import { TFunction } from "i18next";
import { BUILT_IN_ZONE_TYPE_IDS, isBuiltInZoneType } from "./zoneDraft";
import { zoneTypeId } from "./zoneTypeId";

/** Geotab's documented defaults: Customer orange, Office light orange, Home green. */
const BUILT_IN_COLORS: Record<(typeof BUILT_IN_ZONE_TYPE_IDS)[number], ZoneType["fillColor"]> = {
  ZoneTypeCustomerId: { r: 255, g: 165, b: 0, a: 160 },
  ZoneTypeOfficeId: { r: 255, g: 200, b: 120, a: 160 },
  ZoneTypeHomeId: { r: 76, g: 175, b: 80, a: 160 },
};

const BUILT_IN_KEYS: Record<(typeof BUILT_IN_ZONE_TYPE_IDS)[number], string> = {
  ZoneTypeCustomerId: "customer",
  ZoneTypeOfficeId: "office",
  ZoneTypeHomeId: "home",
};

/**
 * `Get ZoneType` returns built-in types as bare id strings (`"ZoneTypeCustomerId"`) and others as
 * objects. Everything after this works with objects that carry at least an id.
 */
export function normalizeZoneTypes(raw: unknown[]): ZoneType[] {
  return raw.flatMap(entry => {
    if (typeof entry === "string") {
      return [{ id: entry, name: entry, comment: "", isBuiltIn: isBuiltInZoneType({ id: entry }) } as ZoneType];
    }
    const id = zoneTypeId(entry);
    return id ? [{ ...(entry as ZoneType), id }] : [];
  });
}

/** `Get ZoneType` may leave out the built-in types; zones still reference them, so they are always listed. */
export function withBuiltInZoneTypes(types: ZoneType[]): ZoneType[] {
  const builtIns = BUILT_IN_ZONE_TYPE_IDS.map(id => {
    const loaded = types.find(type => type.id === id);
    return { id, name: id, comment: "", ...loaded, isBuiltIn: true, fillColor: loaded?.fillColor ?? BUILT_IN_COLORS[id] } as ZoneType;
  });
  return [...builtIns, ...types.filter(type => !isBuiltInZoneType(type))];
}

export function zoneTypeLabel(type: Pick<ZoneType, "id" | "name">, t: TFunction): string {
  const key = BUILT_IN_KEYS[type.id as keyof typeof BUILT_IN_KEYS];
  return key ? t(`zonesPage.builtInTypes.${key}`) : type.name || type.id;
}

export function sortZoneTypes(types: ZoneType[], t: TFunction): ZoneType[] {
  return [...types].sort(
    (a, b) =>
      Number(isBuiltInZoneType(b)) - Number(isBuiltInZoneType(a)) ||
      zoneTypeLabel(a, t).localeCompare(zoneTypeLabel(b, t), "de"),
  );
}
